import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CalendarIcon, Loader2, CheckCircle, User, Sparkles } from "lucide-react";
import { useInfluencerData } from "@/hooks/useInfluencerData";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { z } from "zod";
import { motion } from "framer-motion";

const bookingSchema = z.object({
  requesterName: z.string().trim().min(2, "Name must be at least 2 characters").max(100),
  requesterEmail: z.string().trim().email("Invalid email address").max(255),
  requesterPhone: z.string().trim().max(20).optional(),
  companyName: z.string().trim().max(100).optional(),
  campaignType: z.string().min(1, "Please select a campaign type"),
  campaignDescription: z.string().trim().max(2000).optional(),
});

interface Package {
  name: string;
  description: string;
  duration_seconds: number;
  price: number;
}

const BookInfluencer = () => {
  const { influencerId } = useParams<{ influencerId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  const { influencer, isLoading: influencerLoading } = useInfluencerData(influencerId);
  const isRTL = language === 'ar';
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Form state
  const [requesterName, setRequesterName] = useState("");
  const [requesterEmail, setRequesterEmail] = useState("");
  const [requesterPhone, setRequesterPhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [campaignType, setCampaignType] = useState("");
  const [campaignDescription, setCampaignDescription] = useState("");
  const [selectedPackage, setSelectedPackage] = useState("");
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();

  // Get packages from influencer
  const packages = (influencer as any)?.packages as Package[] | undefined;

  // Auto-fill user data when logged in
  useEffect(() => {
    if (user) {
      if (!requesterEmail) setRequesterEmail(user.email || "");
      if (!requesterName) {
        const name = user.user_metadata?.full_name || user.user_metadata?.name || "";
        setRequesterName(name);
      }
    }
  }, [user]);

  // Pre-select package from URL
  useEffect(() => {
    const packageParam = searchParams.get("package");
    if (packageParam && packages) {
      const found = packages.find(p => p.name === packageParam);
      if (found) {
        setSelectedPackage(found.name);
      }
    }
  }, [searchParams, packages]);

  const validateForm = () => {
    try {
      bookingSchema.parse({
        requesterName,
        requesterEmail,
        requesterPhone: requesterPhone || undefined,
        companyName: companyName || undefined,
        campaignType,
        campaignDescription: campaignDescription || undefined,
      });
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach(err => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    if (!influencerId) return;

    setIsSubmitting(true);

    try {
      // Get selected package price for budget
      const pkg = packages?.find(p => p.name === selectedPackage);
      const budget = pkg?.price || null;

      const { error } = await supabase.from("influencer_bookings").insert({
        influencer_id: influencerId,
        requester_id: user?.id || null,
        requester_name: requesterName.trim(),
        requester_email: requesterEmail.trim(),
        requester_phone: requesterPhone.trim() || null,
        company_name: companyName.trim() || null,
        campaign_type: campaignType,
        campaign_description: selectedPackage 
          ? `Package: ${selectedPackage}\n\n${campaignDescription}`.trim()
          : campaignDescription.trim() || null,
        budget,
        preferred_start_date: startDate ? format(startDate, "yyyy-MM-dd") : null,
        preferred_end_date: endDate ? format(endDate, "yyyy-MM-dd") : null,
        status: "pending",
      });

      if (error) throw error;

      // Send notifications (non-blocking, parallel)
      const notificationPromises = [
        // Notify influencer
        supabase.functions.invoke("send-booking-notification", {
          body: {
            influencerId,
            requesterName: requesterName.trim(),
            requesterEmail: requesterEmail.trim(),
            companyName: companyName.trim() || undefined,
            campaignType,
            campaignDescription: selectedPackage 
              ? `Package: ${selectedPackage}\n\n${campaignDescription}`.trim()
              : campaignDescription.trim() || undefined,
            budget: budget || undefined,
            preferredStartDate: startDate ? format(startDate, "PPP") : undefined,
            preferredEndDate: endDate ? format(endDate, "PPP") : undefined,
          },
        }),
        // Send confirmation to requester
        supabase.functions.invoke("send-booking-confirmation", {
          body: {
            type: "influencer",
            requesterName: requesterName.trim(),
            requesterEmail: requesterEmail.trim(),
            targetName: influencer?.name,
            companyName: companyName.trim() || undefined,
            campaignType: campaignType.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
            selectedPackage: selectedPackage || undefined,
            budget: budget || undefined,
            preferredStartDate: startDate ? format(startDate, "PPP") : undefined,
            dashboardUrl: user ? `${window.location.origin}/dashboard` : undefined,
          },
        }),
      ];

      Promise.all(notificationPromises).catch(err => 
        console.error("Email notification error:", err)
      );

      setIsSuccess(true);
      toast({
        title: isRTL ? "تم إرسال الطلب!" : "Booking Request Sent!",
        description: isRTL 
          ? "سيقوم المؤثر بمراجعة طلبك والتواصل معك قريباً."
          : "The influencer will review your request and get back to you soon.",
      });
    } catch (error: any) {
      console.error("Booking error:", error);
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL 
          ? "فشل في إرسال الطلب. يرجى المحاولة مرة أخرى."
          : "Failed to submit booking request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (influencerLoading) {
    return (
      <div className="container mx-auto px-4 py-12 flex justify-center items-center min-h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!influencer) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold mb-4">
          {isRTL ? "المؤثر غير موجود" : "Influencer Not Found"}
        </h1>
        <Button asChild variant="outline">
          <Link to="/influencer-booking">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {isRTL ? "العودة للمؤثرين" : "Back to Influencers"}
          </Link>
        </Button>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <motion.div 
        className="container mx-auto px-4 py-16 max-w-lg text-center"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="p-8 rounded-2xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20">
          <div className="h-16 w-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
            <CheckCircle className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold mb-3">
            {isRTL ? "تم إرسال الطلب!" : "Request Submitted!"}
          </h1>
          <p className="text-muted-foreground mb-2">
            {isRTL 
              ? `تم إرسال طلب الحجز الخاص بك لـ ${influencer.name}.`
              : `Your booking request for ${influencer.name} has been sent.`}
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            {isRTL 
              ? "تم إرسال تأكيد إلى بريدك الإلكتروني."
              : "A confirmation has been sent to your email."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild variant="outline">
              <Link to={`/influencers/${influencerId}`}>
                {isRTL ? "عرض الملف الشخصي" : "View Profile"}
              </Link>
            </Button>
            <Button asChild>
              <Link to="/influencer-booking">
                {isRTL ? "تصفح المزيد من المؤثرين" : "Browse More Influencers"}
              </Link>
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  const selectedPkg = packages?.find(p => p.name === selectedPackage);

  return (
    <motion.div 
      className="container mx-auto px-4 py-12 max-w-3xl"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Back Button */}
      <div className="mb-6">
        <Button variant="outline" asChild className="border-primary/10 hover:bg-primary/5">
          <Link to={`/influencers/${influencerId}`}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {isRTL ? "العودة للملف الشخصي" : "Back to Profile"}
          </Link>
        </Button>
      </div>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">
          {isRTL ? `حجز ${influencer.name}` : `Book ${influencer.name}`}
        </h1>
        <p className="text-muted-foreground">
          {isRTL ? "أكمل النموذج أدناه لإرسال طلب حجز." : "Fill out the form below to send a booking request."}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* User logged in indicator */}
          {user && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-primary/5 p-3 rounded-lg border border-primary/10">
              <User className="h-4 w-4 text-primary" />
              <span>
                {isRTL 
                  ? `مسجل الدخول باسم ${user.email}`
                  : `Logged in as ${user.email}`}
              </span>
              <Badge variant="secondary" className="ml-auto">
                <Sparkles className="h-3 w-3 mr-1" />
                {isRTL ? "تم الملء التلقائي" : "Auto-filled"}
              </Badge>
            </div>
          )}

          {/* Contact Information */}
          <Card className="border-primary/10">
            <CardHeader>
              <CardTitle className="text-lg">
                {isRTL ? "معلوماتك" : "Your Information"}
              </CardTitle>
              <CardDescription>
                {isRTL ? "أدخل تفاصيل الاتصال الخاصة بك" : "Enter your contact details"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{isRTL ? "الاسم الكامل *" : "Full Name *"}</Label>
                  <Input
                    id="name"
                    value={requesterName}
                    onChange={(e) => setRequesterName(e.target.value)}
                    placeholder={isRTL ? "اسمك" : "Your name"}
                    className={errors.requesterName ? "border-destructive" : ""}
                  />
                  {errors.requesterName && (
                    <p className="text-sm text-destructive">{errors.requesterName}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{isRTL ? "البريد الإلكتروني *" : "Email *"}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={requesterEmail}
                    onChange={(e) => setRequesterEmail(e.target.value)}
                    placeholder="your@email.com"
                    className={errors.requesterEmail ? "border-destructive" : ""}
                  />
                  {errors.requesterEmail && (
                    <p className="text-sm text-destructive">{errors.requesterEmail}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">{isRTL ? "رقم الهاتف" : "Phone Number"}</Label>
                  <Input
                    id="phone"
                    value={requesterPhone}
                    onChange={(e) => setRequesterPhone(e.target.value)}
                    placeholder="+966 5XX XXX XXXX"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">{isRTL ? "اسم الشركة" : "Company Name"}</Label>
                  <Input
                    id="company"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder={isRTL ? "شركتك (اختياري)" : "Your company (optional)"}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Campaign Details */}
          <Card className="border-primary/10">
            <CardHeader>
              <CardTitle className="text-lg">
                {isRTL ? "تفاصيل الحملة" : "Campaign Details"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="type">{isRTL ? "نوع الحملة *" : "Campaign Type *"}</Label>
                  <Select value={campaignType} onValueChange={setCampaignType}>
                    <SelectTrigger className={errors.campaignType ? "border-destructive" : ""}>
                      <SelectValue placeholder={isRTL ? "اختر النوع" : "Select type"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sponsored_post">{isRTL ? "منشور ممول" : "Sponsored Post"}</SelectItem>
                      <SelectItem value="product_review">{isRTL ? "مراجعة منتج" : "Product Review"}</SelectItem>
                      <SelectItem value="brand_ambassador">{isRTL ? "سفير العلامة التجارية" : "Brand Ambassador"}</SelectItem>
                      <SelectItem value="event_appearance">{isRTL ? "ظهور في فعالية" : "Event Appearance"}</SelectItem>
                      <SelectItem value="live_stream">{isRTL ? "بث مباشر" : "Live Stream"}</SelectItem>
                      <SelectItem value="other">{isRTL ? "أخرى" : "Other"}</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.campaignType && (
                    <p className="text-sm text-destructive">{errors.campaignType}</p>
                  )}
                </div>
                
                {packages && packages.length > 0 && (
                  <div className="space-y-2">
                    <Label>{isRTL ? "اختر الباقة" : "Select Package"}</Label>
                    <Select value={selectedPackage} onValueChange={setSelectedPackage}>
                      <SelectTrigger>
                        <SelectValue placeholder={isRTL ? "اختر باقة" : "Choose a package"} />
                      </SelectTrigger>
                      <SelectContent>
                        {packages.map((pkg, index) => (
                          <SelectItem key={index} value={pkg.name}>
                            {pkg.name} - {pkg.price.toLocaleString()} SAR
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Selected Package Summary with Fee Breakdown */}
              {selectedPkg && (
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{selectedPkg.name}</span>
                    <Badge variant="secondary">{selectedPkg.duration_seconds}s</Badge>
                  </div>
                  {selectedPkg.description && (
                    <p className="text-sm text-muted-foreground mb-3">{selectedPkg.description}</p>
                  )}
                  
                  {/* Price Breakdown */}
                  <div className="border-t border-primary/10 pt-3 mt-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{isRTL ? "سعر الباقة" : "Package Price"}</span>
                      <span>{selectedPkg.price.toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{isRTL ? "رسوم المنصة (10%)" : "Platform Fee (10%)"}</span>
                      <span>{(selectedPkg.price * 0.1).toLocaleString()} SAR</span>
                    </div>
                    <div className="flex justify-between font-semibold pt-2 border-t border-primary/10">
                      <span>{isRTL ? "الإجمالي" : "Total"}</span>
                      <Badge className="bg-primary">{(selectedPkg.price * 1.1).toLocaleString()} SAR</Badge>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isRTL ? "تاريخ البدء المفضل" : "Preferred Start Date"}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !startDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startDate ? format(startDate, "PPP") : (isRTL ? "اختر التاريخ" : "Select date")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={setStartDate}
                        disabled={(date) => date < new Date()}
                        initialFocus
                        className="p-3 pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>{isRTL ? "تاريخ الانتهاء المفضل" : "Preferred End Date"}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !endDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, "PPP") : (isRTL ? "اختر التاريخ" : "Select date")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        disabled={(date) => date < (startDate || new Date())}
                        initialFocus
                        className="p-3 pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">
                  {isRTL ? "وصف الحملة" : "Campaign Description"}
                </Label>
                <Textarea
                  id="description"
                  value={campaignDescription}
                  onChange={(e) => setCampaignDescription(e.target.value)}
                  placeholder={isRTL 
                    ? "صف حملتك وأهدافك وأي متطلبات محددة..."
                    : "Describe your campaign, goals, and any specific requirements..."}
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <Button 
            type="submit" 
            size="lg" 
            className="w-full bg-gradient-to-r from-primary to-primary/80 hover:shadow-lg transition-all"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isRTL ? "جاري الإرسال..." : "Submitting..."}
              </>
            ) : (
              isRTL ? "إرسال طلب الحجز" : "Submit Booking Request"
            )}
          </Button>
        </div>
      </form>
    </motion.div>
  );
};

export default BookInfluencer;
